import React, { useEffect, useRef, useState } from 'react';
import { IconBook } from './Icons';

export type TurnStyle = 'book' | 'curl' | 'wave' | 'roll' | 'shutter' | 'cube' | 'fade' | 'slide' | 'zoom' | 'blinds' | 'card' | 'none';
export const TURN_STYLES: TurnStyle[] = ['book', 'curl', 'wave', 'roll', 'shutter', 'cube', 'fade', 'slide', 'zoom', 'blinds', 'card', 'none'];
export const isTurnStyle = (v: unknown): v is TurnStyle => typeof v === 'string' && (TURN_STYLES as string[]).includes(v);
export const TURN_NAME: Record<TurnStyle, string> = {
  book: 'Book page',
  curl: 'Corner curl',
  wave: 'Wave',
  roll: 'Scroll roll',
  shutter: 'Shutters',
  cube: '3D cube',
  fade: 'Fade',
  slide: 'Slide',
  zoom: 'Zoom out',
  blinds: 'Blinds',
  card: 'Card flip',
  none: 'No effect',
};
export const TURN_LABEL: Record<TurnStyle, string> = {
  book: 'Page turning: like a book',
  wave: 'Page turning: a wave rolls up the page',
  roll: 'Page turning: the page rolls up like a scroll',
  curl: 'Page turning: the corner peels and curls',
  shutter: 'Page turning: shutters open across the page',
  cube: 'Page turning: the page turns like a 3D cube',
  fade: 'Page turning: the old page fades into the new one',
  slide: 'Page turning: the pages slide past each other',
  zoom: 'Page turning: the old page zooms away',
  blinds: 'Page turning: blinds tilt open across the page',
  card: 'Page turning: the page flips over like a card',
  none: 'Page turning: no effect',
};
const KEY = 'rj-turn';

export function getTurnStyle(): TurnStyle {
  try {
    const v = localStorage.getItem(KEY) as TurnStyle | null;
    if (v && TURN_STYLES.includes(v)) return v;
  } catch {
    /* not remembered */
  }
  return 'book';
}
export function setTurnStyle(s: TurnStyle): void {
  try {
    localStorage.setItem(KEY, s);
  } catch {
    /* ignore */
  }
}

export interface Flip {
  style: TurnStyle;
  dir: 'next' | 'prev';
  from: string; // the page that was open
  to: string; // the page that opens
  id: number;
}

const N = 20; // strips of the wave
const MS: Record<TurnStyle, number> = { book: 800, wave: 1050, roll: 900, curl: 1000, shutter: 1000, cube: 850, fade: 650, slide: 700, zoom: 750, blinds: 1000, card: 950, none: 0 };

/** The picture of a page turning, drawn over the page while the new one shows beneath. */
export function PageFlip({ flip, viewH, onDone }: { flip: Flip; viewH: number; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, MS[flip.style] + 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flip.id]);

  const { style, dir } = flip;
  // the effect plays over the part of the page that is on screen, so it is seen from start to end
  const box = { height: Math.max(200, Math.round(viewH)) };
  const base = dir === 'prev' ? <img className="flip-base" src={flip.from} alt="" /> : null;

  if (style === 'wave') {
    const src = dir === 'next' ? flip.from : flip.to;
    return (
      <div className={'flip wave ' + dir} style={box} aria-hidden="true" key={flip.id}>
        {base}
        <div className="strips">
          {Array.from({ length: N }, (_, i) => (
            <i
              key={i}
              style={{
                backgroundImage: `url("${src}")`,
                backgroundPosition: `0 ${-Math.round((i * box.height) / N)}px`,
                ['--i' as string]: i,
                ['--dx' as string]: Math.round(Math.sin(i * 0.9) * 22) + 'px',
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (style === 'roll') {
    const src = dir === 'next' ? flip.from : flip.to;
    return (
      <div className={'flip roll ' + dir} style={box} aria-hidden="true" key={flip.id}>
        {base}
        <img className="roll-page" src={src} alt="" />
        <div className="roll-bar" />
      </div>
    );
  }

  if (style === 'shutter') {
    const K = 10;
    const src = dir === 'next' ? flip.from : flip.to;
    return (
      <div className={'flip shutter ' + dir} style={box} aria-hidden="true" key={flip.id}>
        {base}
        <div className="cols">
          {Array.from({ length: K }, (_, i) => (
            <i key={i} style={{ backgroundImage: `url("${src}")`, backgroundPosition: `${(i / (K - 1)) * 100}% 0`, ['--i' as string]: i }} />
          ))}
        </div>
      </div>
    );
  }

  if (style === 'cube') {
    return (
      <div className={'flip cube ' + dir} style={box} aria-hidden="true" key={flip.id}>
        <img className="cube-new" src={flip.to} alt="" />
        <img className="cube-old" src={flip.from} alt="" />
      </div>
    );
  }

  if (style === 'fade' || style === 'zoom') {
    return (
      <div className={'flip ' + style + ' ' + dir} style={box} aria-hidden="true" key={flip.id}>
        <img className="fx-old" src={flip.from} alt="" />
      </div>
    );
  }

  if (style === 'slide' || style === 'card') {
    return (
      <div className={'flip ' + style + ' ' + dir} style={box} aria-hidden="true" key={flip.id}>
        <img className="fx-new" src={flip.to} alt="" />
        <img className="fx-old" src={flip.from} alt="" />
      </div>
    );
  }

  if (style === 'blinds') {
    const R = 12;
    return (
      <div className={'flip blinds ' + dir} style={box} aria-hidden="true" key={flip.id}>
        <div className="rows">
          {Array.from({ length: R }, (_, i) => (
            <i
              key={i}
              style={{
                backgroundImage: `url("${flip.from}")`,
                backgroundPosition: `0 ${-Math.round((i * box.height) / R)}px`,
                ['--i' as string]: dir === 'next' ? i : R - 1 - i,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (style === 'curl') return <Curl flip={flip} box={box} onDone={onDone} />;

  // book
  return (
    <div className={'flip book ' + dir} style={box} aria-hidden="true" key={flip.id}>
      {base}
      <img className="flip-leaf" src={dir === 'next' ? flip.from : flip.to} alt="" />
    </div>
  );
}

type Pt = [number, number];
function clipHalf(poly: Pt[], f: (p: Pt) => number): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const fa = f(a), fb = f(b);
    if (fa >= 0) out.push(a);
    if ((fa >= 0) !== (fb >= 0)) {
      const t = fa / (fa - fb);
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
    }
  }
  return out;
}
const css = (pts: Pt[]) => (pts.length < 3 ? 'polygon(0 0, 0 0, 0 0)' : 'polygon(' + pts.map(([x, y]) => `${x.toFixed(1)}px ${y.toFixed(1)}px`).join(',') + ')');

/** The corner of the page peels back along a diagonal fold; the turned-over part shows the paper's back. */
function Curl({ flip, box, onDone }: { flip: Flip; box: { height: number }; onDone: () => void }) {
  const wrap = useRef<HTMLDivElement>(null);
  const page = useRef<HTMLImageElement>(null);
  const back = useRef<HTMLDivElement>(null);
  const [, setReady] = useState(false);
  const next = flip.dir === 'next';

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const w = el.clientWidth, h = box.height;
    const rect: Pt[] = [[0, 0], [w, 0], [w, h], [0, h]];
    const T = 950;
    const t0 = performance.now();
    let raf = 0;
    const draw = (p: number) => {
      const s = p * (w + h) * 1.02;
      const c = w + h - s;
      const vis = clipHalf(rect, ([x, y]) => c - x - y);
      let fl = clipHalf(rect, ([x, y]) => x + y - c);
      fl = clipHalf(fl, ([, y]) => c - y);
      fl = clipHalf(fl, ([, y]) => w - (c - y));
      fl = clipHalf(fl, ([x]) => c - x);
      fl = clipHalf(fl, ([x]) => h - (c - x));
      if (page.current) page.current.style.clipPath = css(vis);
      if (back.current) {
        back.current.style.clipPath = css(fl);
        back.current.style.transform = `matrix(0,-1,-1,0,${c},${c})`;
        back.current.style.display = fl.length < 3 ? 'none' : 'block';
      }
    };
    const tick = (now: number) => {
      const k = Math.min(1, (now - t0) / T);
      const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      draw(next ? e : 1 - e);
      if (k < 1) raf = requestAnimationFrame(tick);
      else onDone();
    };
    draw(next ? 0 : 1);
    setReady(true);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flip.id]);

  return (
    <div className="flip curl" ref={wrap} style={box} aria-hidden="true" key={flip.id}>
      {!next && <img className="flip-base" src={flip.from} alt="" />}
      <img ref={page} className="curl-page" src={next ? flip.from : flip.to} alt="" />
      <div ref={back} className="curl-back" />
    </div>
  );
}

/** The button and the list of page-turning styles. */
export function TurnMenu({ value, onChange }: { value: TurnStyle; onChange: (s: TurnStyle) => void }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent | TouchEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', away);
    document.addEventListener('touchstart', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('touchstart', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return (
    <span className="info-item turn-menu" ref={box}>
      <button className="icon-btn turn-btn" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} aria-label={TURN_LABEL[value] + ' (press to change)'} title={TURN_LABEL[value] + ' — press to change'}>
        <IconBook />
      </button>
      {open && (
        <div className="info-card turn-list" role="menu" aria-label="Page turning style">
          {TURN_STYLES.map((s) => (
            <button key={s} role="menuitemradio" aria-checked={s === value} className={s === value ? 'on' : ''} onClick={() => { onChange(s); setOpen(false); }}>
              {s === value ? '● ' : '○ '}{TURN_NAME[s]}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
